import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

/**
 * The Colours page.
 *
 * One list, read row by row through `data-field` — never by a form-wide
 * indexed name, which breaks silently when a middle row is deleted and saves
 * the wrong entry against the wrong colour.
 */
export function initColorsPage(): void {
  const found = $<HTMLFormElement>('[data-colors-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  wireList(form);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/colors?guildId=${guildId}`, {
        guildId,
        palette: rows(form)
          .map((row) => ({
            name: text(row, 'name'),
            hex: text(row, 'hex') || '#5865f2',
            /*
             * Sent back untouched.
             *
             * Drop it and every save reads as "entry deleted, entry added": the
             * server would destroy the Discord role members are wearing and
             * provision a fresh one on the next request.
             */
            roleId: hidden(row, 'roleId'),
            requiredRoleIds: findSelectMenu(row, 'requiredRoleIds')?.value ?? [],
          }))
          // A colour with no name is a row somebody started and left. Sending
          // it would fail validation and lose the rest of the page with it.
          .filter((entry) => entry.name !== ''),
      });
    },
  });
}

/* ------------------------------------------------------------------ the rows */

function wireList(form: HTMLFormElement): void {
  const add = $<HTMLButtonElement>('[data-add="color"]', form);
  add?.addEventListener('click', () => {
    addRow(form);
  });

  // Delegated from the list: a row added after load would otherwise need its
  // own binding, and forgetting it gives a delete button that does nothing.
  const list = $<HTMLElement>('[data-list="color"]', form);
  list?.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('[data-remove]');
    if (!button) return;

    button.closest('[data-rule-row]')?.remove();
    refreshEmptyState(form);
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });

  refreshEmptyState(form);
}

function addRow(form: HTMLFormElement): void {
  const template = $<HTMLTemplateElement>('[data-template="color"]');
  const list = $<HTMLElement>('[data-list="color"]', form);
  if (!template || !list) return;

  const holder = document.createElement('div');
  holder.innerHTML = template.innerHTML;

  const row = holder.querySelector('[data-rule-row]');
  if (!row) return;

  list.append(row);

  // The blueprint's `<select>` tags are inert markup inside a `<template>` and
  // were never upgraded with the rest of the page.
  upgradeSelects(document);

  refreshEmptyState(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));

  row.querySelector<HTMLInputElement>('input[data-field="name"]')?.focus();
}

function refreshEmptyState(form: HTMLFormElement): void {
  const note = $<HTMLElement>('[data-empty="color"]', form);
  const list = $<HTMLElement>('[data-list="color"]', form);
  if (note) note.hidden = (list?.querySelectorAll('[data-rule-row]').length ?? 0) > 0;
}

function rows(form: HTMLFormElement): HTMLElement[] {
  return $$<HTMLElement>('[data-list="color"] [data-rule-row]', form);
}

/* ---------------------------------------------------------------- the fields */

function field(row: ParentNode, name: string): Element | null {
  return row.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

function text(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.value.trim() : '';
}

/** A hidden field carries a value the page never shows and must not lose. */
function hidden(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.value : '';
}
